import { Component, EventEmitter, OnInit, Output, Type as AngularCoreType, Renderer2, ViewEncapsulation, ElementRef, ViewChild, AfterViewInit, OnDestroy, } from '@angular/core';
import { NG_VALUE_ACCESSOR } from '@angular/forms';
import { FormFieldPropertyEnum } from '../../../models/form-builder/form-field-property-enum.model';
import { ControlType, ValidationInfo } from '../form-field-base/form-field-base.component';
import { TextInputFormFieldBaseComponent } from '../input-form-field-base/text-input-form-field-base.component';
import { FormFieldOnInitPropertyEnum } from '../../../models/form-builder/form-field-on-init-output-property.enum';
import { ValueConcatenatorService } from '../../../services/value-concatenator.service';
import { FieldValue } from './extras';
import { FormulaEvaluator } from '../formula-form-field/formula-evaluator';
import { DisplayFormatEnum } from '../../../models/form-builder/form-field.model';

// See https://community-dc.max.gov/x/h4Gzmw for overview of how this field type is implemented

@Component({
    selector: 'app-value-concatenator-form-field',
    templateUrl: './value-concatenator-form-field.component.html',
    styleUrls: ['./value-concatenator-form-field.component.scss', '../form-fields.scss'],
    encapsulation: ViewEncapsulation.None,
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: ValueConcatenatorFormFieldComponent,
            multi: true
        }
    ],
    standalone: false
})
export class ValueConcatenatorFormFieldComponent extends TextInputFormFieldBaseComponent implements OnInit, OnDestroy {
    // When a form instance first loads we need to know the values of all fields referenced in the FormField's formula - that what this is for
    private allFieldValuesOnLoadSubscription: any;

    // This gets populated with those fields from allFieldValuesOnLoadSubscription that the FormField's formula references
    private allRelevantFieldValues: FieldValue[] = [];

    // When a field's value changes we need to know about it and if it's a referenced field update our value
    private fieldValueUpdatedSubscription: any;

    // an array of all the field names used in the field's formula (populated on initialization)
    private fieldsReferencedInFormula: string[];


    @Output() onInit = new EventEmitter();

    private readonly formFieldProperties: string[] =
        [
            FormFieldPropertyEnum.NAME,
            FormFieldPropertyEnum.FIELD_GROUP,
            FormFieldPropertyEnum.DISPLAY_NAME,
            FormFieldPropertyEnum.GRID_COLUMN_WIDTH,
            FormFieldPropertyEnum.HELP_TEXT,
            FormFieldPropertyEnum.TOOL_TIP,
            FormFieldPropertyEnum.DISPLAY_FORMAT, // NOTE:  temporarily using display format to display optional validations.
            FormFieldPropertyEnum.INSTRUCTIONS_TEXT,
            FormFieldPropertyEnum.CONCATENATION_FORMULA,
            FormFieldPropertyEnum.IS_USED_TO_SET_FORM_INSTANCE_CONCATENATED_ID
        ];

    private readonly displayFormats: string[] =
        [
            DisplayFormatEnum.READONLY_FIELD,
            DisplayFormatEnum.URL_BUTTON
        ];

    private readonly validationOptions: ValidationInfo[] = [];

    @ViewChild('gridOnlyTextArea') gridOnlyTextAreaElement: ElementRef;
    
    // Constructor.
    constructor(rendererParam: Renderer2, private fieldValueConcatenatorService: ValueConcatenatorService) {
        super(rendererParam);

        this.matInputId = this.generateUniqueId('value-concatenator');

        return;
    }

    // Life cycle methods.
    public ngOnInit(): void {
        // populate fieldsReferencedInFormula based on fields referenced in the formula
        const formula = this.formField.formula;
        this.fieldsReferencedInFormula = FormulaEvaluator.getTokenNames(formula);

        // Set a default constraint value.
        if (this.FormField) {
            if (!this.FormField.constraintName) {
                this.FormField.constraintName = "None";
            }
        }

        let hshOnInitProperties = this.getProperties();

        this.onInit.emit(hshOnInitProperties);


        // Subscribe to get the values of _all_ of a form instance's fields upon form instance load and populate this.allRelevantFieldValues
        // with the values of those fields that are referenced in the formula
        this.allFieldValuesOnLoadSubscription = this.fieldValueConcatenatorService.AllFieldValuesOnLoad.subscribe((fieldValues: FieldValue[]) => {
            for (let fieldValue of fieldValues) {
                const nameFound: string = fieldValue.nameFound(this.fieldsReferencedInFormula);
                if (nameFound) {
                    //console.log(`nameFound=${nameFound}`);
                    // toLowercase needed because that's how the formfield's formula is rendered
                    this.allRelevantFieldValues[nameFound.toLowerCase()] = this.prepValue(fieldValue);
                }
            }
            //console.log(this.allRelevantFieldValues);
        });

        // Subscribe to updates made to fields, and if it's for a field that this field's formula references, revaluate the formula and update the form control
        this.fieldValueUpdatedSubscription = this.fieldValueConcatenatorService.LastUpdatedFieldValue.subscribe((fieldValue: FieldValue) => {
            const nameFound: string = fieldValue.nameFound(this.fieldsReferencedInFormula);
            if (nameFound) {
                this.allRelevantFieldValues[nameFound.toLowerCase()] = this.prepValue(fieldValue);
                this.FormControl.setValue(FormulaEvaluator.concatenateValues(null, formula, this.allRelevantFieldValues));
            }
        });
    }

    // Implement abstract methods.
    public getProperties(): any {

        let hshOnInitProperties = {
            component: this,
            formField: this.FormField,
            properties: this.formFieldProperties,
            displayFormatValues: this.displayFormats
        };
        hshOnInitProperties[FormFieldOnInitPropertyEnum.REQUIRED_PREVIEW_INSTANCE_MODE_HEIGHT] = 190;
        hshOnInitProperties[FormFieldOnInitPropertyEnum.REQUIRED_PREVIEW_INSTANCE_MODE_HEIGHT_UNIT] = 'px';

        return hshOnInitProperties;
    }

    // Override the method used to get my class.
    public getFormFieldClass(): AngularCoreType<any> {
        return (ValueConcatenatorFormFieldComponent);
    }

    public ngOnDestroy(): void {
        this.fieldValueUpdatedSubscription.unsubscribe();
        this.allFieldValuesOnLoadSubscription.unsubscribe();
    }

    public get NumGridOnlyTextAreaRows(): string {
        // Note:  if property this.gridOnlyTextAreaElement is resolved, use it.
        let numRowsText: string = '2';

        return numRowsText;
    }

    public openUrl() {
        window.open(this.FormControl.value, "_blank");
    }

    public get ButtonLabel(): string {
        // TODO
        let label = this.formField.transientUrlBuilderButtonLabel?.trim();
        if (label) {
            return label;
        } else {
            return this.formField.displayName ?? this.formField.name;
        }
    }

    public get DisplayAsInput(): boolean {
        return !this.formField.displayFormat || this.formField.displayFormat == DisplayFormatEnum.READONLY_FIELD;
    }

    public get DisplayAsUrlButton(): boolean {
        return this.formField.displayFormat == DisplayFormatEnum.URL_BUTTON;
    }

    // This is a life-cycle method called from FormFieldBaseComponent.writeValue()
    protected writeValueTriggered(): void {
        if ((this.Mode === 'preview') || (this.Mode === 'instance')) {
            if (this.ControlType === ControlType.REACTIVE_FORMS) {
                this.SetupFormControlFromWriteValue();
                this.FormControl.disable();
            }
        }
        // Make sure the user doesn't get a "you have unsaved changes" message
        this.FormInstanceElement.UserUpdatedData = false;
    }

    private prepValue(fieldValue: FieldValue): any {
        return fieldValue.value?.replace(/,|$/g, '').replace(/$/g, '');
    }

}
